// =====================================================================
// aiCreditEstimates — coarse, *display-only* per-job credit estimates
// used by the Composer pre-flight courtesy check. Not a hard gate; the
// real debit happens server-side in the AI router after the call.
//
// Source of truth = `ai_job_credit_costs.client_estimate` (server-managed,
// editable by super-admins via Admin → Bulk ops). Fetched once via
// `loadCreditEstimates()` with an in-memory cache; falls back to the
// hardcoded `ESTIMATED_CREDITS` map below if the DB read fails or the
// cache is cold (so `estimateCreditsForJob` stays sync-callable).
// =====================================================================

import { supabase } from "@/integrations/supabase/client";

export type AiJobKind =
  | "brief_draft"        // Writing Agent draft (also covers refine passes)
  | "brief_outline"      // outline-only generation (planning helper)
  | "excerpt_assist"     // small excerpt-level helper
  | "composer_chat_turn";// composer AI chat copilot turn

/** Fallback values, used when the DB cache is cold or unavailable. */
export const ESTIMATED_CREDITS: Record<AiJobKind, number> = {
  brief_draft: 30,
  brief_outline: 12,
  excerpt_assist: 3,
  composer_chat_turn: 2,
};

// Server-side `ai_job_credit_costs.job_kind` keys differ from the
// client's display-only `AiJobKind` keys. Map client → server here so
// the cache can resolve from a single DB fetch.
const CLIENT_TO_DB_JOB_KIND: Partial<Record<AiJobKind, string>> = {
  brief_draft: "brief_draft",
  brief_outline: "brief_plan",
  composer_chat_turn: "composer_chat_turn",
  // excerpt_assist has no server row → always falls back to ESTIMATED_CREDITS.
};

const CACHE_TTL_MS = 5 * 60_000; // 5 minutes
let _cache: { loadedAt: number; map: Partial<Record<AiJobKind, number>> } | null = null;
let _inflight: Promise<Partial<Record<AiJobKind, number>>> | null = null;

const fetchEstimatesFromDb = async (): Promise<Partial<Record<AiJobKind, number>>> => {
  const { data, error } = await supabase
    .from("ai_job_credit_costs")
    .select("job_kind, client_estimate");
  if (error) throw error;
  const byDbKind = new Map<string, number>();
  for (const row of (data ?? []) as Array<{ job_kind: string; client_estimate: number }>) {
    byDbKind.set(row.job_kind, Number(row.client_estimate));
  }
  const map: Partial<Record<AiJobKind, number>> = {};
  for (const [clientKind, dbKind] of Object.entries(CLIENT_TO_DB_JOB_KIND) as Array<
    [AiJobKind, string]
  >) {
    const v = byDbKind.get(dbKind);
    if (typeof v === "number") map[clientKind] = v;
  }
  return map;
};

/**
 * Async loader — returns DB-backed estimates and warms the cache.
 * Safe to call repeatedly; concurrent calls share an in-flight promise.
 */
export const loadCreditEstimates = async (): Promise<Partial<Record<AiJobKind, number>>> => {
  const now = Date.now();
  if (_cache && now - _cache.loadedAt < CACHE_TTL_MS) return _cache.map;
  if (_inflight) return _inflight;
  _inflight = (async () => {
    try {
      const map = await fetchEstimatesFromDb();
      _cache = { loadedAt: Date.now(), map };
      return map;
    } catch (err) {
      console.warn("loadCreditEstimates failed; using fallback", err);
      return {};
    } finally {
      _inflight = null;
    }
  })();
  return _inflight;
};

/**
 * Sync accessor — uses warm cache if available, else falls back to the
 * hardcoded ESTIMATED_CREDITS map. Callers that want freshest values
 * should `await loadCreditEstimates()` first.
 */
export const estimateCreditsForJob = (kind: AiJobKind): number => {
  const cached = _cache?.map[kind];
  if (typeof cached === "number") return cached;
  return ESTIMATED_CREDITS[kind] ?? 10;
};

